import React from 'react';
import { Alert } from 'components';
import s from './styles.module.scss';

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      errorType: 'general'
    };
  }

  static getDerivedStateFromError(error) {
    // Determine error type based on error message or stack
    let errorType = 'general';
    
    if (error.message.includes('404') || error.message.includes('not found')) {
      errorType = 'notFound';
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      errorType = 'network';
    } else if (error.message.includes('auth') || error.message.includes('unauthorized')) {
      errorType = 'auth';
    } else if (error.message.includes('permission') || error.message.includes('forbidden')) {
      errorType = 'permission';
    }

    return { 
      hasError: true, 
      error,
      errorType,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('🚨 Route Error Boundary Caught Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      errorType: this.state.errorType,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Show user-friendly error message based on error type
    this.showErrorMessage();
  }

  showErrorMessage = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'notFound':
        Alert.error('The page you are looking for could not be found.');
        break;
      case 'network':
        Alert.error('Network error. Please check your connection and try again.');
        break;
      case 'auth':
        Alert.error('Authentication required. Please log in to continue.');
        break;
      case 'permission':
        Alert.error('You do not have permission to access this page.');
        break;
      default:
        Alert.error('Something went wrong. Please try refreshing the page.');
    }
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      errorType: 'general'
    });
  };

  handleGoHome = () => {
    window.location.href = '/home';
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleRefresh = () => {
    window.location.reload();
  };

  getErrorMessage = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'notFound':
        return {
          title: 'Page Not Found',
          message: 'The page you are looking for doesn\'t exist or has been moved.',
          icon: 'search_off'
        };
      case 'network':
        return {
          title: 'Connection Error',
          message: 'Please check your internet connection and try again.',
          icon: 'wifi_off'
        };
      case 'auth':
        return {
          title: 'Authentication Required',
          message: 'Please log in to access this page.',
          icon: 'lock'
        };
      case 'permission':
        return {
          title: 'Access Denied',
          message: 'You don\'t have permission to view this page.',
          icon: 'block'
        };
      default:
        return {
          title: 'Something Went Wrong',
          message: 'An unexpected error occurred. Please try again.',
          icon: 'error_outline'
        };
    }
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.getErrorMessage();
      
      return (
        <div className={s.errorBoundary}>
          <div className={s.errorContainer}>
            <div className={s.errorIcon}>
              <span className="material-icons">{errorMessage.icon}</span>
            </div>
            
            <h1 className={s.errorTitle}>{errorMessage.title}</h1>
            
            <p className={s.errorMessage}>
              {errorMessage.message}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={s.errorDetails}>
                <summary>Error Details (Development)</summary>
                <div className={s.errorStack}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <strong>Error ID:</strong> {this.state.errorId}
                  <br />
                  <strong>Error Type:</strong> {this.state.errorType}
                  <br />
                  <strong>Component Stack:</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className={s.errorActions}>
              <button 
                className={s.retryButton}
                onClick={this.handleRetry}
              >
                <span className="material-icons">refresh</span>
                Try Again
              </button>
              
              <button 
                className={s.homeButton}
                onClick={this.handleGoHome}
              >
                <span className="material-icons">home</span>
                Go Home
              </button>
              
              <button 
                className={s.refreshButton}
                onClick={this.handleGoBack}
              >
                <span className="material-icons">arrow_back</span>
                Go Back
              </button>
            </div>

            <div className={s.errorFooter}>
              <p>If this problem persists, please contact support.</p>
              <p>Error ID: {this.state.errorId}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary; 